import React from 'react'
import { EuiBadge } from '@equipmentshare/ds2'
import moment from 'moment'
import { calculateBusinessDays } from './dateValidation'
import { DemandRequestLineItemStatus } from '../../../graphql'

export type DemandRequestStatus = 'Open' | 'Assigned' | 'Completed' | 'Cancelled'

export type RequestUrgency =
	| { isUrgent: true, urgencyType: 'overdue' }
	| { isUrgent: true, urgencyType: 'today' }
	| { isUrgent: true, urgencyType: 'upcoming', daysUntilNeeded: number }

export type DemandRequestStatusData = {
	dateCompleted?: string | null,
	dateCancelled?: string | null,
	assignedToUser?: {
		id: string,
		firstName?: string | null,
		lastName?: string | null,
	} | null,
	lineItems?: {
		neededBy?: string | null,
	}[],
}

export const getDemandRequestStatus = ( request: DemandRequestStatusData ): DemandRequestStatus => {
	if ( request.dateCancelled ) {
		return 'Cancelled'
	}

	if ( request.dateCompleted ) {
		return 'Completed'
	}

	if ( request.assignedToUser ) {
		return 'Assigned'
	}

	return 'Open'
}

/**
 * Gets urgency information for a demand request based on the closest need by date
 * @param request - The demand request to check
 * @returns Object with isUrgent flag and daysUntilNeeded, or null if not urgent
 */
export const getRequestUrgency = ( request: DemandRequestStatusData ): RequestUrgency | null => {
	// Don't show urgency for completed or cancelled requests
	if ( request.dateCompleted || request.dateCancelled ) {
		return null
	}

	if ( !request.lineItems || request.lineItems.length === 0 ) {
		return null
	}

	// Cache current moment to avoid repeated function calls
	const now = moment()
	const today = moment().startOf( 'day' )

	// Find the closest need by date among all line items
	const neededByDates = request.lineItems
		.map( ( item ) => item.neededBy )
		.filter( ( date ): date is string => !!date )
		.map( ( date ) => moment( date ) )
		.filter( ( date ) => date.isValid() )

	if ( neededByDates.length === 0 ) {
		return null
	}

	// Sort to find the earliest date
	neededByDates.sort( ( a, b ) => a.diff( b ) )
	const closestDate = neededByDates[ 0 ]

	if ( closestDate.isBefore( today ) ) {
		return { isUrgent: true, urgencyType: 'overdue' }
	}

	if ( closestDate.isSame( today ) ) {
		return { isUrgent: true, urgencyType: 'today' }
	}

	// Calculate business days until needed
	const businessDaysUntil = calculateBusinessDays( now, closestDate )

	// Check if urgent (less than 4 business days)
	if ( businessDaysUntil < 4 ) {
		return {
			isUrgent: true,
			urgencyType: 'upcoming',
			daysUntilNeeded: businessDaysUntil,
		}
	}

	return null
}

export const DemandRequestStatusBadge: React.FC<{ request: DemandRequestStatusData }> = ( { request } ) => {
	const status = getDemandRequestStatus( request )
	const urgency = getRequestUrgency( request )

	const statusBadge = ( () => {
		switch ( status ) {
			case 'Cancelled':
				return (
					<EuiBadge color="danger">
						Cancelled
					</EuiBadge>
				)
			case 'Completed':
				return (
					<EuiBadge color="success" iconType="checkInCircleFilled">
						Completed
					</EuiBadge>
				)
			case 'Assigned':
				return (
					<EuiBadge color="primary" iconType="user">
						Assigned
					</EuiBadge>
				)
			case 'Open':
			default:
				return (
					<EuiBadge color="default" iconType="dot">
						Open
					</EuiBadge>
				)
		}
	} )()

	// If there's no urgency, just return the status badge
	if ( !urgency ) {
		return statusBadge
	}

	// If urgent, show both badges inline with spacing
	let urgencyBadge: React.ReactNode
	if ( urgency.urgencyType === 'overdue' ) {
		urgencyBadge = <EuiBadge color="danger" iconType="clock">Overdue</EuiBadge>
	} else if ( urgency.urgencyType === 'today' ) {
		urgencyBadge = <EuiBadge color="danger" iconType="clock">Need Today</EuiBadge>
	} else {
		urgencyBadge = (
			<EuiBadge color="warning" iconType="clock">
				Need {urgency.daysUntilNeeded}d
			</EuiBadge>
		)
	}

	return (
		<span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
			{statusBadge}
			{urgencyBadge}
		</span>
	)
}


export const DemandRequestAssignmentStatus: React.FC<{ request: DemandRequestStatusData }> = ( { request } ) => (
	request.assignedToUser ? (
		<EuiBadge color="primary" iconType="user">
			{`${request.assignedToUser.firstName} ${request.assignedToUser.lastName}`}
		</EuiBadge>
	)
		: <>-</>
)

/**
 * Checks if a line item is in a terminal state (cannot be modified)
 * Terminal states are states that represent a final outcome for the line item
 * @param status - The status of the line item
 * @returns true if the line item is in a terminal state
 */
export const isLineItemInTerminalState = ( status?: DemandRequestLineItemStatus | null ): boolean => {
	if ( !status ) {
		return false
	}

	return status === DemandRequestLineItemStatus.Completed || status === DemandRequestLineItemStatus.Cancelled
}

/**
 * Checks if a line item can be cancelled
 * @param status - The status of the line item
 * @returns true if the line item can be cancelled
 */
export const canCancelLineItem = ( status?: DemandRequestLineItemStatus | null ): boolean => {
	return !isLineItemInTerminalState( status )
}

/**
 * Checks if a user can cancel a demand request or its line items based on permissions
 * User can cancel if:
 * - They have CAN_EDIT_DEMAND_REQUEST_BY_STORE_ID permission for the owning inventory
 *   (the fulfilling inventory, or the main DC when a routed stock request has none), OR
 * - They are the requesting user AND the request is NOT in assigned state
 * @param options - Object containing permission check parameters
 * @returns true if the user can cancel
 */
export const canUserCancelDemandRequest = ( options: {
	owningInventoryId: string,
	requestingUserId?: string | null,
	assignedToUserId?: string | null,
	authenticatedUserId: string,
	storesWithEditPermission: string[],
} ): boolean => {
	const {
		owningInventoryId,
		requestingUserId,
		assignedToUserId,
		authenticatedUserId,
		storesWithEditPermission,
	} = options

	// Check if user has CAN_EDIT_DEMAND_REQUEST_BY_STORE_ID permission for the owning inventory
	const hasEditPermission = storesWithEditPermission.includes( owningInventoryId )
	if ( hasEditPermission ) {
		return true
	}

	// Check if user is the requesting user AND request is NOT in assigned state
	const isRequestingUser = requestingUserId === authenticatedUserId
	const isAssigned = !!assignedToUserId

	return isRequestingUser && !isAssigned
}
